import styled from 'styled-components';
import { space, SpaceProps } from 'styled-system';
import React from 'react';
import { getLuminance } from 'polished';
import CheckmarkIcon from 'ui/icons/checkmark.svg?react';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import media from 'ui/media';

const Button = styled.button`
  ${space};
  appearance: none;
  border: 0;
  padding: 0;
  display: block;
  cursor: pointer;
  background: none;
  position: relative;
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.feedback.focus};
  }

  ${media.mobile`
  display: flex;
  flex-direction: column;
  align-items: center;
  `}
`;

// @see https://www.shutterstock.com/support/article/what-is-a-color-swatch
const ColorSwatchBackground = styled.div`
  position: relative;
  height: ${({ theme }) => theme.space.xxxl};
  width: ${({ theme }) => theme.space.xxxl};
  ${media.mobile`
  height: ${({ theme }) => theme.space.xxl};
  width: ${({ theme }) => theme.space.xxl};
  `}
  @media (max-width: 280px) {
    height: 42px;
    width: 42px;
  }

  display: inline-grid;
  justify-items: center;
  align-items: center;
  border: 2px solid white;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
  backface-visibility: hidden;
  ${/* sc-selector */ Button}:hover &,
  ${/* sc-selector */ Button}[data-focus-visible-added]:focus & {
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2),
      0 0 0 2px ${({ theme }) => theme.colors.feedback.focus};
  }
`;

// prettier-ignore
const SelectedIndicator = styled(CheckmarkIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  opacity: 0.7;
  transition: opacity 0.2s;
  ${Button}[aria-pressed='false'] & {
    display: none;
  }
  ${ /* sc-selector */ Button}:hover & {
    opacity: 0.9;
  }
`;

export type ColorSwatchProps = SpaceProps &
  React.HTMLAttributes<HTMLButtonElement> & {
    color: string;
    label?: string;
    maxWidth?: string;
  };

const ColorSwatch = ({ color, label, maxWidth = '4rem', ...props }: ColorSwatchProps) => {
  const checkmarkColor = getLuminance(color) >= getLuminance('#777777') ? 'black' : 'white';
  return (
    <Button {...props} color={color} style={{ maxWidth }}>
      <ColorSwatchBackground style={{ backgroundColor: color }}>
        <SelectedIndicator color={checkmarkColor} />
      </ColorSwatchBackground>
      {label && <Body type={ThemeFontStyleTypeEnum.bodySmall}>{label}</Body>}
    </Button>
  );
};

export default ColorSwatch;
